import Tooltip from "@/Components/Tooltip/Tooltip";
import useIsLiveMerge from "@/Pages/Auth/Bestellingen/Live/useIsLiveMerge";
import useLiveOrdersHook from "@/shared/hooks/useLiveOrdersHook";
import useOrderInformationHook from "@/shared/hooks/useOrderInformationHook";
import usePaymentHook from "@/shared/hooks/usePaymentHook";
import useSourceHook from "@/shared/hooks/useSourceHook";
import useUserSettingsHook from "@/shared/hooks/useUserSettingsHook";
import { cn } from "@/shared/utils/cn";
import {
  deliveryMethodsEnum,
  minute,
  newLabelShowMinute,
} from "@/shared/utils/constant";
import {
  DeliveryMethods,
  OrderStatus,
  PaymentStatusEnum,
} from "@/shared/utils/enum";
import { formatDate } from "@/shared/utils/helpers";
import useOrderHelper from "@/shared/v2/hooks/useOrderHelper";
import { OrderV2 } from "@/shared/v2/queries/interfacesV2";
import dayjs, { UnitType } from "dayjs";
import React from "react";
import { renderToStaticMarkup } from "react-dom/server";
import { useTranslation } from "react-i18next";
import useTimerHook from "../../shared/hooks/useTimerHook";
import { Alert } from "../../shared/utils/animations";
import Badge from "../Badge/Badge";
import Center from "../Base/Center";
import Checkbox from "../Checkbox/Checkbox";
import Icon from "../Icon/Icon";
import AlertIcon from "./AlertIcon";
import OrderAddress from "./OrderAddress";
import OrderCardBranchImage from "./OrderCardBranchImage";
import OrderCardEditButton from "./OrderCardEditButton";
import OrderCardPrice from "./OrderCardPrice";
import OrderCardPrintButton from "./OrderCardPrintButton";
import OrderExternalDriverStatus from "./OrderExternalDriverStatus";
import OrderKitchenStatus from "./OrderKitchenStatus";
import OrderTooltip from "./OrderTooltip";

function iconFromOrderMeta(
  size: string | undefined,
  fill: string | undefined
): { className: string; style?: React.CSSProperties } {
  const s = size ?? "2.5rem";
  const sizeMap: Record<string, string> = {
    "2.5rem": "h-10 w-10",
    "3rem": "h-12 w-12",
    "3.5rem": "h-14 w-14",
    "2rem": "h-8 w-8",
    "1.5rem": "h-6 w-6",
    "2.75rem": "h-[2.75rem] w-[2.75rem]",
    "4rem": "h-16 w-16",
  };
  const dim = sizeMap[s] ?? `h-[${s}] w-[${s}]`;
  if (!fill) return { className: dim };
  if (fill.startsWith("#")) return { className: dim, style: { fill } };
  return { className: cn(dim, `fill-${fill}`) };
}

interface OrderCardProps {
  isDragging?: boolean;
  onTimeClick?: () => void;
  onClick?: () => void;
  order: OrderV2;
  showTipsOnLive: boolean;
  courier?: string;
  hasSwitch?: boolean;
  showOrderCount: boolean;
  actionButtonType?: "print" | "edit";
}

const Container = React.forwardRef<any, any>(
  ({ zipcodeColorCode, className = "", style, ...props }, ref) => (
    <div
      ref={ref}
      className={`relative w-full ${className}`.trim()}
      style={{
        borderTop:
          zipcodeColorCode && zipcodeColorCode !== "var(--white)"
            ? `0.5rem solid ${zipcodeColorCode}`
            : "none",
        borderTopLeftRadius: "7.5%",
        borderTopRightRadius: "3%",
        ...style,
      }}
      {...props}
    />
  )
);

const Inner = ({
  alert,
  className = "",
  justifyContent,
  bg,
  width,
  minHeight,
  boxShadow,
  ...props
}) => (
  <div
    className={`flex ${className}`.trim()}
    style={{
      animation: alert ? `${Alert} 2.75s linear forwards` : undefined,
      justifyContent,
      background: `var(--${bg})`,
      width,
      minHeight,
      boxShadow,
    }}
    {...props}
  />
);

const NewBadgeContainer = ({
  className = "",
  children,
}: {
  className?: string;
  children: React.ReactNode;
}) => <div className={cn("animate-pulse", className)}>{children}</div>;

const LeftColumn = ({ className = "", ...props }) => (
  <div
    className={`flex flex-col items-center justify-between p-2 ${className}`.trim()}
    {...props}
  />
);

const MainContent = ({ className = "", ...props }) => (
  <div
    className={`flex w-full flex-col justify-between p-2 ${className}`.trim()}
    {...props}
  />
);

const RightColumn = ({ className = "", ...props }) => (
  <div
    className={`relative flex flex-[0_0_auto] items-center bg-white p-5 text-btn-small font-medium text-dark ${className}`.trim()}
    {...props}
  />
);

const TimeDisplay = ({ className = "", ...props }) => (
  <div
    className={`flex flex-col items-center font-semibold ${className}`.trim()}
    {...props}
  />
);

interface ValueProps {
  color?: string;
  italic?: boolean;
  fontWeight?: number;
  fontSize?: string;
}
const Value = ({
  as: Component = "div",
  color,
  italic,
  fontWeight,
  fontSize,
  letterSpacing,
  mr,
  ml,
  marginBottom,
  maxWidth,
  style,
  ...props
}: ValueProps & any) => (
  <Component
    style={{
      fontSize: fontSize || "1.6rem",
      fontWeight: fontWeight || 600,
      color: color ? `var(--${color})` : "var(--dark)",
      fontStyle: italic ? "italic" : undefined,
      letterSpacing,
      marginRight: mr,
      marginLeft: ml,
      marginBottom,
      maxWidth,
      ...style,
    }}
    {...props}
  />
);

const StatusBadge = ({ className = "", ...props }) => (
  <div
    className={`text-xl font-bold text-error ${className}`.trim()}
    {...props}
  />
);

const TipBadge = ({ className = "", ...props }) => (
  <div
    className={`max-w-full overflow-hidden text-ellipsis whitespace-nowrap bg-white p-[0.2rem] text-[1.1rem] font-bold text-error ${className}`.trim()}
    {...props}
  />
);

const OrderNumber = ({ className = "", ...props }) => (
  <div className={`text-[1.75rem] font-black ${className}`.trim()} {...props} />
);

const Bar = ({ percentage }: { percentage: number }) => (
  <div
    className="relative h-3 w-full overflow-hidden bg-white"
    style={{ transition: "all 250ms ease" }}
  >
    <div
      className="absolute left-0 top-0 h-3 w-full bg-error"
      style={{
        transition: "all 250ms ease",
        transform: `translateX(-${percentage}%)`,
      }}
    />
  </div>
);

const OrderCard = React.forwardRef<any, OrderCardProps>(
  (
    {
      isDragging = false,
      onTimeClick,
      onClick,
      order,
      showTipsOnLive,
      courier,
      hasSwitch,
      showOrderCount,
      actionButtonType = "edit",
      ...props
    },
    ref
  ) => {
    const { t } = useTranslation("");
    const { update } = useLiveOrdersHook();
    const [isMerge] = useIsLiveMerge();
    const {
      isWebsite,
      isPos,
      daily_receipt_number,
      isUber,
      delivery_method_id: deliveryMethodId,
      source: _source,
      table_number,
      isAsap,
      placed_date_time: placedTime,
      hand_over_time: actualHandOverTime,
      hand_over_end_time: handOverEndTime,
      public_order_number: publicId,
      order_status: status,
      payment_method_id: paymentMethod,
      order_payment_status: orderPaymentStatus,
      tip,
      hand_over_opening_time_date: handOverOpeningTimeDate,
      placed_date_opening_time_date: placedDateOpeningTimeDate,
      zipcode_color_code: zipcodeColorCode,
      is_custom_time_slot_selected: isCustomTimeSlotSelected,
      custom_time_slot_label: customTimeSlotLabel,
      remarks,
      branchGroup,
    } = useOrderInformationHook(order);
    const { name } = useOrderHelper(order);
    const { isPosOrderAcceptedAuto } = useUserSettingsHook();
    const { getPayment } = usePaymentHook();
    const { getSource } = useSourceHook();

    let handOverTime = actualHandOverTime
      ? dayjs(actualHandOverTime)
      : undefined;

    if (handOverEndTime && handOverTime) {
      const time = handOverEndTime.split(" ")[1];
      const [hours, minutes] = time?.split(":") ?? handOverEndTime.split(":");

      handOverTime = handOverTime
        .set("hours", parseInt(hours))
        .set("minutes", parseInt(minutes));
    }

    const difference = (type: UnitType = "hour") =>
      handOverTime ? dayjs(handOverTime).diff(dayjs(), type) : 0;
    const timerData = useTimerHook(difference("minute"));
    const placedTimeDiff = dayjs().diff(dayjs(placedTime), "minute");
    const hours = timerData / minute;
    const minHours = 5;
    const percentage = hours <= minHours ? (100 / minHours) * hours : 100;

    const delivery = t(`${deliveryMethodsEnum[deliveryMethodId]}`);
    const tipExist = Number(tip || 0) > 0;
    const payment = getPayment(paymentMethod);
    const source = getSource(_source);

    const showHandOverDate =
      dayjs(handOverOpeningTimeDate) > dayjs(placedDateOpeningTimeDate) &&
      !dayjs(handOverOpeningTimeDate).isToday();

    const handleCheckbox = (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();
      const currentStatus = order.order_information.order_status;

      update.mutateAsync({
        branch: order.order_information.branch_id,
        orderId: order.order_information.item_id,
        status:
          currentStatus !== OrderStatus.HANDED_OVER ? "handed_over" : "enroute",
      });
    };

    const handleCardClick = () => {
      if (
        status === OrderStatus.ARRIVED &&
        !(
          isPosOrderAcceptedAuto &&
          orderPaymentStatus === PaymentStatusEnum.PENDING_PAYMENT
        )
      ) {
        onTimeClick?.();
      } else {
        onClick?.();
      }
    };

    const getBackgroundColor = () => {
      if ([3, 4].includes(deliveryMethodId)) {
        return "order-bg-pos";
      }
      if ([2, 9].includes(deliveryMethodId)) {
        return "order-bg-takeaway";
      }
      return "order-bg";
    };

    const isNewOrder = placedTimeDiff < newLabelShowMinute;
    const showUnpaid = status === OrderStatus.UNPAID_BUT_VISIBLE;
    const showArrivedAlert = status === OrderStatus.ARRIVED;
    const restaurantName =
      isMerge && branchGroup?.branch?.name
        ? `${branchGroup.branch.name} `
        : null;

    return (
      <Container
        {...props}
        className="my-2 md:m-2"
        ref={ref}
        zipcodeColorCode={zipcodeColorCode}
        onClick={handleCardClick}
      >
        {hasSwitch && (
          <Tooltip
            content={
              <div
                dangerouslySetInnerHTML={{
                  __html: renderToStaticMarkup(<OrderTooltip order={order} />),
                }}
              />
            }
          >
            <div>
              <div
                onClick={handleCheckbox}
                className="bg-[white] absolute left-[-2rem] top-[-1rem] ml-[1rem]"
              >
                <Checkbox active={status === OrderStatus.HANDED_OVER} />
              </div>
            </div>
          </Tooltip>
        )}
        <Inner
          alert={status === 1}
          justifyContent="space-between"
          bg={isDragging ? "purple" : getBackgroundColor()}
          width="100%"
          minHeight="10rem"
          boxShadow="0px 0px 15px 0px rgba(0, 0, 0, 0.28)"
        >
          <div className="relative flex w-full gap-2">
            <LeftColumn>
              <Center className="h-full">
                {isNewOrder && (
                  <NewBadgeContainer className="absolute left-0.5 top-1 bg-white color-error px-2">
                    <StatusBadge>{t("new")}</StatusBadge>
                  </NewBadgeContainer>
                )}

                {isWebsite || isPos ? (
                  <Tooltip
                    content={`${
                      isMerge ? `${branchGroup?.branch?.name} -` : ""
                    } ${t(table_number ? "table" : source?.translationKey || "")}`}
                  >
                    <div className="relative">
                      <div className="-right-2 -bottom-6 absolute">
                        <Icon
                          variants={table_number ? "table" : source?.icon}
                          {...iconFromOrderMeta(
                            table_number ? "2rem" : "1.75rem",
                            "dark"
                          )}
                        />
                      </div>
                      <OrderCardBranchImage branchGroup={branchGroup} />
                    </div>
                  </Tooltip>
                ) : (
                  <Icon
                    variants={source?.icon}
                    {...iconFromOrderMeta("2.5rem", "dark")}
                  />
                )}
              </Center>
              <div className="flex flex-col items-center">
                {showOrderCount && (
                  <OrderNumber>#{daily_receipt_number}</OrderNumber>
                )}
                {showTipsOnLive && tipExist && (
                  <TipBadge>{t("withTip")}</TipBadge>
                )}
              </div>
            </LeftColumn>
            <MainContent>
              <div className="flex h-full flex-col">
                {restaurantName && (
                  <Value fontWeight={900}>{restaurantName}</Value>
                )}
                <div className="flex flex-wrap items-center">
                  <Value letterSpacing="-0.1rem" mr="0.5rem" fontWeight={500}>
                    {publicId}
                  </Value>
                  <OrderCardPrice order={order} />
                  <OrderKitchenStatus order={order} />
                </div>
                <div>
                  {deliveryMethodId === DeliveryMethods.DELIVERY && (
                    <OrderAddress order={order} courier={courier} />
                  )}
                  {deliveryMethodId === DeliveryMethods.TAKEAWAY && (
                    <Value>{name}</Value>
                  )}
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-y-1">
                  <Tooltip content={t(payment?.translationKey || "unknown")}>
                    <div className="flex">
                      <Icon
                        variants={payment?.icon}
                        {...iconFromOrderMeta("2.5rem", "dark")}
                      />
                    </div>
                  </Tooltip>

                  <Value ml="0.5rem" fontSize="1.3rem">
                    {delivery}
                  </Value>

                  <div className="ml-auto pl-2">
                    <OrderExternalDriverStatus order={order} />
                  </div>
                </div>
              </div>
              <div className="flex justify-between">
                {showHandOverDate && handOverTime && (
                  <Value
                    fontSize="1.3rem"
                    marginBottom="0.5rem"
                    fontWeight={900}
                  >
                    {formatDate(handOverTime)}
                  </Value>
                )}

                {showUnpaid && (
                  <Value color="error" italic fontSize="1.4rem">
                    {t("unpaid")}
                  </Value>
                )}
              </div>

              <div className="flex flex-wrap items-center justify-between">
                <Tooltip content={t("placedTime")}>
                  <div>
                    <Value>{formatDate(placedTime, "time")}</Value>
                  </div>
                </Tooltip>
                {actionButtonType === "edit" ? (
                  <OrderCardEditButton order={order} />
                ) : (
                  <OrderCardPrintButton order={order} />
                )}
              </div>
            </MainContent>
          </div>

          <RightColumn
            onClick={(e) => {
              e.stopPropagation();
              onTimeClick?.();
            }}
          >
            <TimeDisplay>
              {isCustomTimeSlotSelected ? (
                <Value fontSize="1.3rem" maxWidth="min-content" as="p">
                  {customTimeSlotLabel}
                </Value>
              ) : (
                <>
                  {isAsap && <Value fontSize="1.3rem">{t("asap")}</Value>}
                  {handOverTime &&
                    (difference("hour") >= 1 ? (
                      <Value fontSize="1.3rem">
                        {formatDate(handOverTime, "time")}
                      </Value>
                    ) : (
                      <Value fontSize="1.3rem">{`${timerData} min`}</Value>
                    ))}
                  {handOverTime && difference("hour") < 1 && (
                    <Value fontSize="1.3rem">
                      {formatDate(handOverTime, "time")}
                    </Value>
                  )}
                  {isUber &&
                    deliveryMethodId ===
                      DeliveryMethods.DELIVERY_BY_EXTERNAL && (
                      <Icon className="h-8 w-8" variants="scooter" />
                    )}
                </>
              )}
            </TimeDisplay>
            {showArrivedAlert && (
              <div className="absolute left-[-1rem] top-[-1rem]">
                <AlertIcon />
              </div>
            )}
            {remarks && (
              <Tooltip content={t("orderHasRemark")}>
                <div>
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      onClick?.();
                    }}
                    className="absolute -left-20 top-[-1rem]"
                  >
                    <Badge />
                  </div>
                </div>
              </Tooltip>
            )}
          </RightColumn>
        </Inner>
        <Bar percentage={percentage} />
      </Container>
    );
  }
);

export default OrderCard;
